import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export type PopiaLegalGroundCode =
  | 'consent'
  | 'contract'
  | 'legal_obligation'
  | 'public_law_duty'
  | 'data_subject_interest'
  | 'legitimate_interest';

export interface ConsentData {
  action: 'accept' | 'reject';
  selectedGrounds: string[];
}

export interface ConsentModalConfig {
  title?: string;
  body?: string[];
  acceptLabel?: string;
  rejectLabel?: string;
  /** When true, show POPIA legal-ground radios and require a selection to accept. */
  requireLegalGround?: boolean;
  /** When true, show Terms / Privacy & POPIA Notice links. */
  showLegalLinks?: boolean;
}

export interface PopiaLegalGroundOption {
  code: PopiaLegalGroundCode;
  label: string;
}

export const POPIA_LEGAL_GROUND_OPTIONS: readonly PopiaLegalGroundOption[] = [
  { code: 'consent', label: 'I have consent from the data subject' },
  { code: 'contract', label: 'I have a contract with the data subject that requires this information' },
  { code: 'legal_obligation', label: 'I am complying with an obligation imposed on me by law' },
  { code: 'public_law_duty', label: 'I am performing a public law duty' },
  { code: 'data_subject_interest', label: 'It protects a legitimate interest of the data subject' },
  {
    code: 'legitimate_interest',
    label: 'It is in my legitimate interest, or that of a third party I act for',
  },
] as const;

export const POPIA_LEGAL_GROUND_CODES: readonly PopiaLegalGroundCode[] = POPIA_LEGAL_GROUND_OPTIONS.map(
  (option) => option.code
);

@Component({
  selector: 'app-consent-modal',
  templateUrl: './consent-modal.component.html',
  styleUrls: ['./consent-modal.component.css'],
})
export class ConsentModalComponent {
  readonly title: string;
  readonly body: string[];
  readonly acceptLabel: string;
  readonly rejectLabel: string;
  readonly requireLegalGround: boolean;
  readonly showLegalLinks: boolean;
  readonly legalGroundOptions = POPIA_LEGAL_GROUND_OPTIONS;
  readonly termsUrl = 'https://thebigdatashowroom.com/terms';
  readonly privacyUrl = 'https://thebigdatashowroom.com/privacy';

  selectedLegalGround: PopiaLegalGroundCode | null = null;

  constructor(
    public dialogRef: MatDialogRef<ConsentModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ConsentModalConfig | null
  ) {
    this.requireLegalGround = data?.requireLegalGround === true;
    this.showLegalLinks = data?.showLegalLinks === true || this.requireLegalGround;
    this.title = data?.title || 'POPIA Acknowledgement';
    this.body =
      data?.body ||
      (this.requireLegalGround
        ? [
            'Each time you run a search on The Big Data Showroom, you act as the Responsible Party under POPIA for the information you receive. This means you must have a valid legal ground for receiving and processing it.',
          ]
        : [
            'Before continuing, confirm that your access is lawful under POPIA and that the contact has consented to future contact where required.',
            'You must use personal information only for authorised business purposes and in line with POPIA obligations.',
          ]);
    this.acceptLabel = data?.acceptLabel || 'I Accept';
    this.rejectLabel = data?.rejectLabel || 'Reject';
  }

  get canAccept(): boolean {
    return !this.requireLegalGround || !!this.selectedLegalGround;
  }

  openExternalLink(event: MouseEvent, url: string): void {
    event.preventDefault();
    event.stopPropagation();
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  accept(): void {
    if (!this.canAccept) {
      return;
    }
    const result: ConsentData = {
      action: 'accept',
      selectedGrounds: this.selectedLegalGround ? [this.selectedLegalGround] : [],
    };
    this.dialogRef.close(result);
  }

  reject(): void {
    const result: ConsentData = {
      action: 'reject',
      selectedGrounds: this.selectedLegalGround ? [this.selectedLegalGround] : [],
    };
    this.dialogRef.close(result);
  }
}
